import { Task, TaskViewer } from "./types";

export interface TaskCapabilities {
  canToggle: (task: Task) => boolean;
  canEditTitle: boolean;
  canChangeOwner: boolean;
  /** The drawer's People: tag and untag people (TD-103). */
  canEditPeople: boolean;
  /** Tags and untags team members. The portal changes only the client's own
   * people, and team tags show there without a remove control (DD-053). */
  canTagTeam: boolean;
  /** The owner field offers Team (no owner). The portal never clears it. */
  canClearOwner: boolean;
  /** The row's client icon and the drawer's "Visibile al cliente" (DD-017,
   * DD-053). Team only. */
  canSeeVisibility: boolean;
  canReorder: boolean;
  canDelete: boolean;
  canCreate: boolean;
  /** The drawer's Links section and its `GET /api/tasks/<id>` (admin only). */
  canSeeLinks: boolean;
  /** The drawer's notes log. Team only (DD-050). */
  canSeeNotes: boolean;
}

const NO_CAPABILITIES: TaskCapabilities = {
  canToggle: () => false,
  canEditTitle: false,
  canChangeOwner: false,
  canEditPeople: false,
  canTagTeam: false,
  canClearOwner: false,
  canSeeVisibility: false,
  canReorder: false,
  canDelete: false,
  canCreate: false,
  canSeeLinks: false,
  canSeeNotes: false,
};

/** What each viewer may do with a task row. UX gating only. The routes are
 * the enforcement boundary. */
export function capabilitiesFor(viewer: TaskViewer): TaskCapabilities {
  switch (viewer) {
    case "admin":
      return {
        canToggle: () => true,
        canEditTitle: true,
        canChangeOwner: true,
        canEditPeople: true,
        canTagTeam: true,
        canClearOwner: true,
        canSeeVisibility: true,
        canReorder: true,
        canDelete: true,
        canCreate: true,
        canSeeLinks: true,
        canSeeNotes: true,
      };
    case "creator":
      return { ...NO_CAPABILITIES, canToggle: (task) => task.assignee?.kind === "team" };
    // A client-role portal session, on its client-visible tasks (DD-053).
    case "client":
      return {
        ...NO_CAPABILITIES,
        canToggle: () => true,
        canEditTitle: true,
        canChangeOwner: true,
        canEditPeople: true,
      };
    // Every other portal session: brief-share links, admin previews, creator rows.
    case "client-viewer":
      return NO_CAPABILITIES;
  }
}

/** What the task drawer shows for a viewer. The creator portal gets view
 * mode only: no links, notes, edit or delete, and so no detail or notes
 * request (DD-017). */
export interface TaskDrawerSections {
  links: boolean;
  notes: boolean;
  edit: boolean;
  delete: boolean;
}

export function drawerSectionsFor(capabilities: TaskCapabilities): TaskDrawerSections {
  return {
    links: capabilities.canSeeLinks,
    notes: capabilities.canSeeNotes,
    edit: capabilities.canEditTitle || capabilities.canChangeOwner || capabilities.canEditPeople,
    delete: capabilities.canDelete,
  };
}
